import { FolderFilled, FolderOutlined } from '@ant-design/icons';
import React from 'react';
import { useGetGlossaryNodeQuery } from '../../../graphql/glossaryNode.generated';
import { EntityType, GlossaryNode, SearchResult } from '../../../types.generated';
import { Entity, EntityCapabilityType, IconStyleType, PreviewType } from '../Entity';
import { EntityProfile, ExportObjectGlossaryType } from '../shared/containers/profile/EntityProfile';
import { SidebarOwnerSection } from '../shared/containers/profile/sidebar/Ownership/sidebar/SidebarOwnerSection';
import { SidebarAboutSection } from '../shared/containers/profile/sidebar/AboutSection/SidebarAboutSection';
import { SidebarDomainSection } from '../shared/containers/profile/sidebar/Domain/SidebarDomainSection';
import { getDataForEntityType } from '../shared/containers/profile/utils';
import { EntityMenuItems } from '../shared/EntityDropdown/EntityDropdown';
import { DocumentationTab } from '../shared/tabs/Documentation/DocumentationTab';
import { PropertiesTab } from '../shared/tabs/Properties/PropertiesTab';
import ChildrenTab from './ChildrenTab';
import { Preview } from './preview/Preview';
import GlossaryRelatedNode from './GlossaryRelatedNode';
import i18n from '../../../i18n/i18n';

type GlossaryNodePreviewProps = GlossaryNode & {
    relationshipType?: string;
};

class GlossaryNodeEntity implements Entity<GlossaryNode> {
    type: EntityType = EntityType.GlossaryNode;

    // Helper function to get translations in class-based entities
    private t = (key: string): string => {
        return i18n.t(key);
    };

    icon = (fontSize: number, styleType: IconStyleType, color?: string) => {
        if (styleType === IconStyleType.TAB_VIEW) {
            return <FolderOutlined style={{ fontSize, color }} />;
        }

        if (styleType === IconStyleType.HIGHLIGHT) {
            return <FolderFilled style={{ fontSize, color: color || '#B37FEB' }} />;
        }

        return (
            <FolderOutlined
                style={{
                    fontSize,
                    color: color || '#BFBFBF',
                }}
            />
        );
    };

    isSearchEnabled = () => true;

    isBrowseEnabled = () => false;

    getAutoCompleteFieldName = () => 'name';

    isLineageEnabled = () => false;

    getPathName = () => 'glossaryNode';

    getCollectionName = () => 'Term Groups';

    getEntityName = () => 'Term Group';

    renderProfile = (urn: string) => {
        const glossaryNodeUrns = [urn];
        const glossaryTermUrns = [];

        /**
         * Represents an object for exporting glossary terms and nodes.
         *
         * @type {{ glossaryTerm: { glossaryNodeUrns: any; glossaryTermUrns: any; }; entityType: string }}
         * @property {Object} glossaryTerm - Contains URNs for glossary nodes and terms.
         * @property {any} glossaryTerm.glossaryNodeUrns - URNs for glossary nodes.
         * @property {any} glossaryTerm.glossaryTermUrns - URNs for glossary terms.
         * @property {string} entityType - The type of entity, in this case, 'glossaryNode'.
         */
        const exportObj: ExportObjectGlossaryType = {
            glossaryTerm: {
                glossaryNodeUrns,
                glossaryTermUrns,
            },
            entityType: 'glossaryTerm',
        };
        return (
            <EntityProfile
                urn={urn}
                entityType={EntityType.GlossaryNode}
                useEntityQuery={useGetGlossaryNodeQuery}
                getOverrideProperties={this.getOverridePropertiesFromEntity}
                isNameEditable
                hideBrowseBar
                isExportable
                exportObj={exportObj}
                tabs={[
                    {
                        name: this.t('Contents'),
                        component: ChildrenTab,
                    },
                    {
                        name: this.t('Documentation'),
                        component: DocumentationTab,
                        properties: {
                            hideLinksButton: true,
                        },
                    },
                    {
                        name: this.t('Related Term Groups'),
                        component: GlossaryRelatedNode,
                        properties: {
                            hideLinksButton: true,
                        },
                    },
                    {
                        name: this.t('Properties'),
                        component: PropertiesTab,
                    },
                ]}
                sidebarSections={[
                    {
                        component: SidebarAboutSection,
                        properties: {
                            hideLinksButton: true,
                        },
                    },
                    {
                        component: SidebarOwnerSection,
                    },

                    {
                        component: SidebarDomainSection,
                    },
                ]}
                headerDropdownItems={
                    new Set([
                        EntityMenuItems.ADD_TERM_GROUP,
                        EntityMenuItems.ADD_TERM,
                        EntityMenuItems.GOVERNANCE_STATUS,
                        EntityMenuItems.GOVERNANCE_FLAG,
                        EntityMenuItems.BULK_IMPORT,
                        EntityMenuItems.MOVE,
                        EntityMenuItems.DELETE,
                        EntityMenuItems.ADD_COMMENT,
                        EntityMenuItems.EXPORT_NODE,
                    ])
                }
            />
        );
    };

    displayName = (data: GlossaryNode) => {
        return data.properties?.name || data.urn;
    };

    getOverridePropertiesFromEntity = (data: GlossaryNode) => {
        return {
            name: this.displayName(data),
        };
    };

    renderSearch = (result: SearchResult) => {
        return this.renderPreview(PreviewType.SEARCH, result.entity as GlossaryNode);
    };

    renderPreview = (_: PreviewType, data: GlossaryNodePreviewProps) => {
        return (
            <Preview
                urn={data?.urn}
                domain={data.domain?.domain}
                parentNodes={data.parentNodes}
                name={this.displayName(data)}
                governanceFlag={data?.governanceFlags}
                description={data?.properties?.description || ''}
                owners={data?.ownership?.owners}
                relationshipType={data?.relationshipType}
            />
        );
    };

    platformLogoUrl = (_: GlossaryNode) => {
        return undefined;
    };

    getGenericEntityProperties = (glossaryNode: GlossaryNode) => {
        return getDataForEntityType({
            data: glossaryNode,
            entityType: this.type,
            getOverrideProperties: (data) => data,
        });
    };

    supportedCapabilities = () => {
        return new Set([
            EntityCapabilityType.OWNERS,
            EntityCapabilityType.DEPRECATION,
            EntityCapabilityType.SOFT_DELETE,
            EntityCapabilityType.DOMAINS,
        ]);
    };
}

export default GlossaryNodeEntity;
